import React, { useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import moment from 'moment'
import { Button, Popover, PopoverBody } from 'reactstrap'
import { DateRange } from 'react-date-range'

import { activePreset, formatDateLabel, presetRanges, toLocalDate, triggerLabel } from './dateRangePresets'

export { formatDateLabel }

const ISO_FORMAT = 'YYYY-MM-DD'

/**
 * The date-range button + popover, shared by ReportToolbar and the
 * Investigate query bar (`InvestigateTab.js`).
 *
 * `id` has to be unique per mounted instance -- reactstrap's `Popover`
 * targets its trigger by DOM id (`target={id}`), so two instances sharing the
 * default would either fight over the same id or have the second popover
 * attach to the first instance's button. ReportToolbar keeps the original
 * `reportsDateRange` id; the Investigate bar passes `investigateDateRange`.
 *
 * While the popover is open the calendar, the editable inputs and the preset
 * rail all edit a local `draft` only -- none of them ever call `onDateChange`
 * or close the popover by themselves. The footer's Apply button is the one
 * place that commits: it calls `onDateChange` once with the draft and then
 * closes. Cancel, Escape, a click outside, and a click on the trigger itself
 * all close and discard the draft instead, with no call.
 *
 * Presets on the left set the draft to that preset's range in one click and
 * nothing more. Which preset is "active" is derived from the draft (not the
 * committed range), so the rail echoes a preset click immediately even
 * before Apply is pressed. The trigger button's label, by contrast, always
 * reflects the committed `startDate`/`endDate` props -- it describes what
 * the report is currently filtered to, not what's mid-edit in the popover.
 * `today` pins the preset clock and exists for tests.
 */
const DateRangeControl = ({ id = 'reportsDateRange', startDate, endDate, onDateChange, className, today }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [draft, setDraft] = useState(null)
    // True from the moment a change-driven focus update lands on an end
    // pick (`[x, 1]`) until a change-driven update settles it back to
    // `[x, 0]`, a preset is clicked, or the popover opens/closes. This is
    // what Apply's `disabled` and the hint text key off of -- unlike
    // `focusedRange[1]`, it is untouched by a bare focus (e.g. the user
    // just clicking into the end field), so tabbing/clicking into an
    // already-complete draft's end input doesn't disable Apply. See
    // `handleRangeFocusChange` for where it's set.
    const [awaitingEnd, setAwaitingEnd] = useState(false)
    // react-date-range's `DateRange.render` spreads `{...this.props}` *after*
    // it sets `onRangeFocusChange={this.handleRangeFocusChange}` (node_modules/
    // react-date-range/dist/components/DateRange/index.js ~line 238-249), so
    // by passing our own `onRangeFocusChange` we replace the library's own
    // focus tracking rather than layering on top of it -- its internal
    // `state.focusedRange` never gets updated by DOM focus again. `DateRange`
    // still reads `this.props.focusedRange || this.state.focusedRange`
    // everywhere it matters (`calcNewSelection`, `setSelection`), so by also
    // controlling `focusedRange` ourselves we become the source of truth
    // instead of losing focus tracking altogether.
    const [focusedRange, setFocusedRange] = useState([0, 0])
    const toggleRef = useRef(null)
    // Mirrors `isOpen` for the readers that run synchronously inside event
    // handlers and can't wait for a re-render. reactstrap's Popover (fade=
    // true) keeps its content mounted and clickable for ~150ms after
    // `isOpen` becomes false while it plays the exit transition, so without
    // an explicit open check a click or focus event landing in that window
    // still reaches handleChange/handleRangeFocusChange/handlePreset/
    // handleApply/handleCancel -- e.g. a double-clicked Apply button's
    // second click would otherwise call `onDateChange` a second time.
    const isOpenRef = useRef(false)
    // True only between an onChange call and the onRangeFocusChange that
    // follows it in the same tick (see handleRangeFocusChange below). It no
    // longer gates a commit -- nothing here auto-commits any more -- but the
    // bare-focus `flushSync` inside handleRangeFocusChange still needs it:
    // without it, merely giving DOM focus to the start-date editable input
    // -- e.g. Tab, or a caller's own `.focus()` -- would look identical to a
    // change-driven focus move, and the flush would run on every focus
    // change instead of only the bare ones that need it (calling it
    // unconditionally would also mean sometimes calling it while a change
    // from this same tick is still mid-flight, which React does not allow
    // -- `flushSync` can't be called from inside `DateInput`'s own setState
    // callback, which is where `handleRangeFocusChange` runs on the changed
    // path; doing so trips React's "flushSync was called from inside a
    // lifecycle method" warning). react-date-range's Calendar wires the
    // start input's onFocus straight to onRangeFocusChange([i, 0]), with no
    // onChange/setSelection in between, so changedRef is what tells "a
    // change just settled" apart from "a field just got bare focus".
    const changedRef = useRef(false)

    // Focus returns to the trigger on every close path (click on the
    // trigger itself, click-outside, Escape, Cancel, or Apply) --
    // reactstrap's Popover (trigger="legacy") funnels the first three
    // through this one `toggle` prop, and Cancel/Apply call `close`
    // directly, so focus is never stranded on document.body. Closing always
    // drops the draft: whatever was mid-edit, applied or not, is discarded.
    const close = () => {
        setIsOpen(false)
        isOpenRef.current = false
        setFocusedRange([0, 0])
        setAwaitingEnd(false)
        setDraft(null)
        changedRef.current = false
        toggleRef.current?.focus()
    }

    const toggle = () => {
        if (isOpen) {
            close()
            return
        }
        setDraft({ startDate: toLocalDate(startDate), endDate: toLocalDate(endDate) })
        setFocusedRange([0, 0])
        setAwaitingEnd(false)
        isOpenRef.current = true
        setIsOpen(true)
    }

    const handleChange = (ranges) => {
        if (!isOpenRef.current) return
        changedRef.current = true
        setDraft({ startDate: ranges.selection.startDate, endDate: ranges.selection.endDate })
    }

    // focusedRange[1] is 1 while an end date is still being picked, but
    // also while the end field simply has focus with nothing mid-pick --
    // e.g. tabbing or clicking into an already-complete draft's end input.
    // It is not, by itself, a reliable "still picking" signal, which is why
    // the hint text and Apply's `disabled` state key off of `awaitingEnd`
    // instead (computed below, from the change-driven transitions only).
    // `focusedRange` itself is still recorded here because the library
    // needs it (DateRange reads `this.props.focusedRange` in
    // calcNewSelection/setSelection).
    //
    // A date typed into an editable input goes through the same
    // single-value path as a calendar click (DateInput.update ->
    // Calendar.onDragSelectionEnd -> onChange(date) -> setSelection(date),
    // one argument, isSingleValue defaults true) for *both* the start and
    // end fields -- there is no separate "start" branch that waits politely
    // for an end date. What actually keeps a typed start date from reading
    // as settled immediately is which field has focus when
    // DateRange.setSelection runs. Typing an end date settles it because the
    // end field already has focus (focusedRange[1] is already 1), so
    // calcNewSelection takes the `endDate = value` branch and
    // nextFocusRange falls through to [0, 0]. Typing a start date and
    // tabbing/clicking to the end field is trickier: DateInput.update fires
    // onChange from inside a setState callback, which under createRoot
    // flushes in a microtask, so the end field's onFocus (a bare
    // onRangeFocusChange([0, 1]) with no onChange) can run *before* that
    // pending blur-driven onChange/setSelection. If we recorded that bare
    // focus immediately, `focusedRange` would already read [0, 1] by the
    // time the blur's setSelection ran, which sends it down the wrong
    // (end-date) branch -- the typed start date would read as though it
    // were an end date, and the hint/Apply-disabled state would follow the
    // wrong field. That's why the bare-focus path below flushes any pending
    // blur-driven selection first, before recording the new focus -- it
    // forces the typed start date through the start branch it belongs on
    // before the end field's focus is allowed to land. Only a real
    // multi-day mouse drag (updateRange) calls setSelection with
    // isSingleValue false, which also lands on [0, 0].
    //
    // `close()` and `toggle()` also reset `focusedRange` to [0, 0] (on
    // close/reopen there is no in-progress pick to preserve), so this is not
    // the only place the value changes -- just the only place a value other
    // than [0, 0] is ever set.
    const handleRangeFocusChange = (nextFocusedRange) => {
        if (!isOpenRef.current) return

        // Captured before the bare-focus branch below (which can, via the
        // flush, recursively re-enter this same function and flip
        // `changedRef.current` back to false) so it reflects whether *this*
        // call was change-driven, not whatever the ref holds by the time we
        // reach the bottom.
        const isChangeDriven = changedRef.current

        if (!changedRef.current) {
            // Bare focus, no onChange in this tick -- flush any pending
            // blur-driven selection (see the comment above) before we
            // record this focus, so a typed start date settles as a start
            // pick before the end field's focus can be observed. This only
            // matters when focus moves programmatically or synchronously --
            // jsdom, a caller's own `el.focus()`, or user-event, all of
            // which can observe the new focus before the pending blur-
            // driven update has flushed. In a real browser, a native Tab or
            // mouse click between two inputs usually has a microtask
            // checkpoint run between the blur and the focus, so this flush
            // is typically a no-op there. Either way it's harmless.
            flushSync(() => {})
            // Defensive: bail if the flush above somehow left the picker
            // closed (nothing here currently closes on its own, but this
            // mirrors the same guard every other handler keeps).
            if (!isOpenRef.current) return
        }

        setFocusedRange(nextFocusedRange)
        // Only a change-driven focus update -- i.e. calcNewSelection just
        // settled a click or a typed/committed date -- moves `awaitingEnd`.
        // A bare focus (tabbing or clicking into a field with nothing
        // mid-pick) leaves it exactly as it was.
        if (isChangeDriven) {
            setAwaitingEnd(nextFocusedRange[1] === 1)
        }
        changedRef.current = false
    }

    const handlePreset = (preset) => {
        if (!isOpenRef.current) return
        setDraft({ startDate: toLocalDate(preset.startDate), endDate: toLocalDate(preset.endDate) })
        setFocusedRange([0, 0])
        setAwaitingEnd(false)
    }

    const handleCancel = () => {
        if (!isOpenRef.current) return
        close()
    }

    const handleApply = () => {
        if (!isOpenRef.current) return
        if (awaitingEnd || !draft) return
        const draftStartIso = moment(draft.startDate).format(ISO_FORMAT)
        const draftEndIso = moment(draft.endDate).format(ISO_FORMAT)
        if (draftStartIso !== startDate || draftEndIso !== endDate) {
            onDateChange({ startDate: draft.startDate, endDate: draft.endDate })
        }
        close()
    }

    const shown = draft || { startDate: toLocalDate(startDate), endDate: toLocalDate(endDate) }
    const presets = presetRanges(today)
    const active = activePreset(moment(shown.startDate).format(ISO_FORMAT), moment(shown.endDate).format(ISO_FORMAT), today)
    const shownStartTime = shown.startDate.getTime()
    const shownEndTime = shown.endDate.getTime()
    // Keyed on the shown dates' getTime() rather than left as `[{ ... }]`
    // inline: a new array every render makes Calendar.componentDidUpdate
    // (react-date-range 1.4.0) see `this.props.ranges[0] !==
    // prevProps.ranges[0]` and call updateShownDate, which snaps the
    // calendar back to the range's month on any re-render mid-selection --
    // e.g. click a start date, navigate to a later month, then focus the
    // end input, and the calendar would jump back to the start date's
    // month instead of staying where the user navigated it.
    const ranges = useMemo(
        () => [{ startDate: shown.startDate, endDate: shown.endDate, key: 'selection' }],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shownStartTime, shownEndTime]
    )

    return (
        <>
            <Button
                id={id}
                innerRef={toggleRef}
                className={`reports-date-button${className ? ` ${className}` : ''}`}
                onClick={toggle}
            >
                <i className="fa fa-calendar" aria-hidden="true"></i>
                {triggerLabel(startDate, endDate, today)}
            </Button>
            <Popover trigger="legacy" placement="bottom" isOpen={isOpen} target={id} toggle={toggle}>
                <PopoverBody className="reports-date-popover">
                    <div className="reports-date-panel">
                        <div className="reports-date-presets" role="group" aria-label="Date range shortcuts">
                            {presets.map((preset) => {
                                const isActive = !!active && active.id === preset.id
                                return (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        className={`reports-date-preset${isActive ? ' is-active' : ''}`}
                                        aria-pressed={isActive}
                                        onClick={() => handlePreset(preset)}
                                    >
                                        {preset.label}
                                    </button>
                                )
                            })}
                        </div>
                        <div className="reports-date-calendar">
                            <DateRange
                                ranges={ranges}
                                focusedRange={focusedRange}
                                onChange={handleChange}
                                onRangeFocusChange={handleRangeFocusChange}
                                editableDateInputs={true}
                                moveRangeOnFirstSelection={false}
                            />
                            <div className="reports-date-footer">
                                <p className="reports-date-hint" role="status">
                                    {awaitingEnd
                                        ? 'Now pick an end date'
                                        : 'Or pick two dates for a custom range'}
                                </p>
                                <button type="button" className="reports-date-cancel" onClick={handleCancel}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="reports-date-apply"
                                    onClick={handleApply}
                                    disabled={awaitingEnd}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </PopoverBody>
            </Popover>
        </>
    )
}

export default DateRangeControl
